import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TableChartIcon from '@mui/icons-material/TableChart';
import { getDependencyGraph } from '../../service/service';
import PageWrapper from 'components/Common/PageWraper';
import CustomizedTable from 'components/CustomizedTable/CustomizedTable';

// ── Types ──────────────────────────────────────────────
interface GraphNode {
  id: string;
  name: string;
}

interface GraphEdge {
  source: string;
  target: string;
}

interface GraphData {
  market: string;
  nodeCount: number;
  edgeCount: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface LayoutNode extends GraphNode {
  x: number;
  y: number;
  level: number;
}

// ── Markets ────────────────────────────────────────────
const MARKETS = [
  { label: 'Benelux', code: 'BENELUX' },
  { label: 'France', code: 'FRANCE' },
];

// ── Status color map (configurable for future use) ─────
export const NODE_STATUS_COLORS: Record<string, string> = {
  default: '#FFFFFF',
  success: '#01C008',
  failed: '#FE2626',
  inProgress: '#ECB914',
  notStarted: '#9E9E9E',
  slaMet: '#01C008',
  slaMissed: '#FE2626',
};

// ── Layout Engine ──────────────────────────────────────
function computeLayout(nodes: GraphNode[], edges: GraphEdge[]): LayoutNode[] {
  if (nodes.length === 0) return [];

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map<string, string[]>();
  const inDeg = new Map<string, number>();

  for (const n of nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }

  for (const e of edges) {
    if (!adj.has(e.source)) continue;
    adj.get(e.source)!.push(e.target);
    inDeg.set(e.target, (inDeg.get(e.target) || 0) + 1);
  }

  const level = new Map<string, number>();
  const queue: string[] = [];

  for (const n of nodes) {
    if ((inDeg.get(n.id) || 0) === 0) {
      level.set(n.id, 0);
      queue.push(n.id);
    }
  }

  if (queue.length === 0) {
    const firstId = nodes[0].id;
    level.set(firstId, 0);
    queue.push(firstId);
  }

  let iterations = 0;
  const maxIterations = nodes.length * nodes.length;
  while (queue.length > 0 && iterations < maxIterations) {
    iterations++;
    const curr = queue.shift()!;
    const currLevel = level.get(curr) || 0;
    for (const child of adj.get(curr) || []) {
      const newLevel = currLevel + 1;
      if (!level.has(child) || level.get(child)! < newLevel) {
        level.set(child, newLevel);
        queue.push(child);
      }
    }
  }

  for (const n of nodes) {
    if (!level.has(n.id)) level.set(n.id, 0);
  }

  const levelGroups = new Map<number, string[]>();
  for (const n of nodes) {
    const l = level.get(n.id) || 0;
    if (!levelGroups.has(l)) levelGroups.set(l, []);
    levelGroups.get(l)!.push(n.id);
  }

  const nodeWidth = 180;
  const nodeHeight = 50;
  const levelGap = 140;
  const nodeGap = 30;
  const paddingX = 60;
  const paddingY = 60;

  const layoutNodes: LayoutNode[] = [];

  for (const [lvl, nodeIds] of levelGroups) {
    nodeIds.forEach((id, idx) => {
      const n = nodeMap.get(id)!;
      layoutNodes.push({
        ...n,
        x: paddingX + idx * (nodeWidth + nodeGap),
        y: paddingY + lvl * (nodeHeight + levelGap),
        level: lvl,
      });
    });
  }

  return layoutNodes;
}

// ── Main Component ─────────────────────────────────────
const GBDOLineagePage: React.FC = () => {
  const { market: initialMarket } = useParams<{ market: string }>();
  const navigate = useNavigate();

  const [selectedMarket, setSelectedMarket] = useState<string>(initialMarket || 'BENELUX');
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTableView, setIsTableView] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const fetchGraph = useCallback(async (mkt: string) => {
    if (!mkt) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDependencyGraph(mkt, 'IBP');
      if (res?.data) {
        setGraphData(res.data);
      } else {
        setGraphData({ market: mkt, nodeCount: 0, edgeCount: 0, nodes: [], edges: [] });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load dependency graph');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGraph(selectedMarket);
  }, [selectedMarket, fetchGraph]);

  const handleMarketChange = (newMarket: string) => {
    navigate(`/data-pipeline/ibpDashboard/lineage/${newMarket}`, { replace: true });
    setSelectedMarket(newMarket);
  };

  // Compute layout
  const layoutNodes = useMemo(() => {
    if (!graphData) return [];
    return computeLayout(graphData.nodes, graphData.edges);
  }, [graphData]);

  const nodeMap = useMemo(
    () => new Map(layoutNodes.map((n) => [n.id, n])),
    [layoutNodes]
  );

  // SVG dimensions
  const svgContentWidth = useMemo(() => {
    if (layoutNodes.length === 0) return 900;
    const maxX = Math.max(...layoutNodes.map((n) => n.x)) + 220;
    return Math.max(maxX, 900);
  }, [layoutNodes]);

  const svgContentHeight = useMemo(() => {
    if (layoutNodes.length === 0) return 600;
    const maxY = Math.max(...layoutNodes.map((n) => n.y)) + 100;
    return Math.max(maxY, 600);
  }, [layoutNodes]);

  // Zoom & pan handlers
  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.2, 0.3));
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setPan({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.min(Math.max(z * delta, 0.3), 3));
  };

  // Build edge paths
  const edgePaths = useMemo(() => {
    const nodeWidth = 180;
    const nodeHeight = 50;
    const paths: { key: string; d: string; fromId: string; toId: string }[] = [];

    for (const edge of graphData?.edges || []) {
      const from = nodeMap.get(edge.source);
      const to = nodeMap.get(edge.target);
      if (!from || !to) continue;

      const x1 = from.x + nodeWidth;
      const y1 = from.y + nodeHeight / 2;
      const x2 = to.x;
      const y2 = to.y + nodeHeight / 2;

      // Straight line
      const d = `M ${x1} ${y1} L ${x2} ${y2}`;

      paths.push({
        key: `${edge.source}-${edge.target}`,
        d,
        fromId: edge.source,
        toId: edge.target,
      });
    }
    return paths;
  }, [graphData, nodeMap]);

  // Build table data from graph
  const tableData = useMemo(() => {
    if (!graphData) return [];

    const nodeMapById = new Map(graphData.nodes.map((n) => [n.id, n.name]));
    const dependencyMap = new Map<string, string[]>();
    const dependentMap = new Map<string, string[]>();

    for (const edge of graphData.edges) {
      if (!dependencyMap.has(edge.source)) dependencyMap.set(edge.source, []);
      dependencyMap.get(edge.source)!.push(nodeMapById.get(edge.target) || edge.target);

      if (!dependentMap.has(edge.target)) dependentMap.set(edge.target, []);
      dependentMap.get(edge.target)!.push(nodeMapById.get(edge.source) || edge.source);
    }

    return graphData.nodes.map((node) => ({
      gdoName: node.name,
      source: (dependentMap.get(node.id) || []).join(', ') || '--',
      landing: (dependencyMap.get(node.id) || []).join(', ') || '--',
    })).sort((a, b) => a.gdoName.localeCompare(b.gdoName)) ;
  }, [graphData]);

  const tableColumns = [
    { key: 'gdoName', name: 'GDO Name', minWidth: 200, sortable: true },
    { key: 'source', name: 'Source (Depends On)', minWidth: 250, sortable: false },
    { key: 'landing', name: 'Landing (Depended By)', minWidth: 250, sortable: false },
  ];

  const nodeWidth = 180;
  const nodeHeight = 50;

  if (loading) {
    return (
      <PageWrapper>
        <Box className="main-bordered-container" bgcolor="backgroundLight.primary">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading dependency graph…</Typography>
          </Box>
        </Box>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Box className="main-bordered-container" bgcolor="backgroundLight.primary">
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Box className="main-bordered-container" bgcolor="backgroundLight.primary">
        <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate('/data-pipeline/programOps/IBP/markets/ibpDashboard')}>
              <ArrowBackIcon color='primary'/>
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0B3A56' }}>
              GDO Lineage
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {/* Market Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ paddingTop: 5 }}>Market</InputLabel>
              <Select
                value={selectedMarket}
                label="Market"
                onChange={(e) => handleMarketChange(e.target.value)}
                sx={{
                  borderColor: '#009BD8',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: '#FFFFFF !important',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  borderRadius: '4px',
                  px: 1.5,
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#009BD8 !important',
                  '& .MuiButton-startIcon, & .MuiButton-label': {
                    whiteSpace: 'nowrap'
                  },
                  '&:hover': { borderColor: '#007ea8' },
                }}
              >
                {MARKETS.map((m) => (
                  <MenuItem key={m.code} value={m.code}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* View Mode Toggle (Switch) */}
            <FormControlLabel
              control={
                <Switch
                  checked={isTableView}
                  onChange={(e) => setIsTableView(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#fff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#009BD8',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isTableView ? (
                    <TableChartIcon sx={{ fontSize: 18, color: '#009BD8' }} />
                  ) : (
                    <AccountTreeIcon sx={{ fontSize: 18, color: '#009BD8' }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#0F3764' }}>
                    {isTableView ? 'Table View' : 'Lineage View'}
                  </Typography>
                </Box>
              }
              sx={{ mr: 0 }}
            />

            {/* Stats */}
            <Chip
              label={`${graphData?.nodeCount || 0} GDOs`}
              size="small"
              sx={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}
            />
            <Chip
              label={`${graphData?.edgeCount || 0} dependencies`}
              size="small"
              sx={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            />
          </Box>

          {/* Content */}
          {!isTableView ? (
            <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
              {/* Zoom Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid #E2E8F0', backgroundColor: '#F1F5F9' }}>
                <Tooltip title="Zoom In">
                  <IconButton size="small" onClick={handleZoomIn}><ZoomInIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                  <IconButton size="small" onClick={handleZoomOut}><ZoomOutIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <IconButton size="small" onClick={handleReset}><CenterFocusStrongIcon /></IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(zoom * 100)}%
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Drag to pan • Scroll to zoom • Hover nodes to highlight connections
                </Typography>
              </Box>

              {/* Graph Canvas */}
              <Box
                sx={{
                  width: '100%',
                  height: 500,
                  overflow: 'hidden',
                  cursor: isDragging.current ? 'grabbing' : 'grab',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
              >
                {layoutNodes.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="text.secondary">
                      No dependencies found for this market. Use the bulk upload to add dependencies.
                    </Typography>
                  </Box>
                ) : (
                  <svg
                    ref={svgRef}
                    width={svgContentWidth}
                    height={svgContentHeight}
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                      transformOrigin: '0 0',
                    }}
                  >
                    <defs>
                      <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="10"
                        refY="3.5"
                        orient="auto"
                      >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#009BD8" />
                      </marker>
                      <marker
                        id="arrowhead-hover"
                        markerWidth="10"
                        markerHeight="7"
                        refX="10"
                        refY="3.5"
                        orient="auto"
                      >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#003771" />
                      </marker>
                    </defs>

                    {/* Edges */}
                    {edgePaths.map((ep) => {
                      const isHighlighted =
                        hoveredNode === ep.fromId || hoveredNode === ep.toId || hoveredEdge === ep.key;
                      return (
                        <path
                          key={ep.key}
                          d={ep.d}
                          fill="none"
                          stroke={isHighlighted ? '#003771' : '#94A3B8'}
                          strokeWidth={isHighlighted ? 2.5 : 1.5}
                          strokeDasharray={isHighlighted ? 'none' : '6,3'}
                          markerEnd={isHighlighted ? 'url(#arrowhead-hover)' : 'url(#arrowhead)'}
                          style={{ transition: 'all 0.2s ease' }}
                          onMouseEnter={() => setHoveredEdge(ep.key)}
                          onMouseLeave={() => setHoveredEdge(null)}
                        />
                      );
                    })}

                    {/* Nodes */}
                    {layoutNodes.map((node) => {
                      const isHovered = hoveredNode === node.id;
                      const isConnected =
                        hoveredNode &&
                        graphData?.edges.some(
                          (e) =>
                            (e.source === hoveredNode && e.target === node.id) ||
                            (e.target === hoveredNode && e.source === node.id)
                        );

                      return (
                        <g
                          key={node.id}
                          onMouseEnter={() => setHoveredNode(node.id)}
                          onMouseLeave={() => setHoveredNode(null)}
                          style={{ cursor: 'pointer' }}
                        >
                          <rect
                            x={node.x}
                            y={node.y}
                            width={nodeWidth}
                            height={nodeHeight}
                            rx={8}
                            ry={8}
                            fill={isHovered ? '#009BD8' : isConnected ? '#E0F2FE' : NODE_STATUS_COLORS.default}
                            stroke={isHovered ? '#003771' : isConnected ? '#009BD8' : '#CBD5E1'}
                            strokeWidth={isHovered || isConnected ? 2 : 1}
                            style={{ transition: 'all 0.2s ease' }}
                          />
                          <text
                            x={node.x + nodeWidth / 2}
                            y={node.y + nodeHeight / 2 - 4}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill={isHovered ? '#FFFFFF' : '#1E293B'}
                            fontSize={12}
                            fontWeight={600}
                            fontFamily="Inter, sans-serif"
                          >
                            {node.name.length > 22 ? node.name.slice(0, 20) + '…' : node.name}
                          </text>

                        </g>
                      );
                    })}
                  </svg>
                )}
              </Box>
            </Box>
          ) : (
            /* Table View */
            <CustomizedTable
              tableTitle="GDO Dependencies"
              tableData={tableData}
              total={tableData.length}
              page={1}
              pageSize={tableData.length || 10}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              onSorting={() => {}}
              sortingInfo={{ sortBy: '', sortDir: '' }}
              defaultColumns={tableColumns.map((c) => c.name)}
              tableType="ibp-gdo-lineage"
              columns={tableColumns}
              hasGroupBy={false}
              isChildren={true}
              isFetching={false}
            />
          )}
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default GBDOLineagePage;
